import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Upload,
  message,
  Select,
  Button,
  Card,
  Typography,
  Divider,
  Row,
  Col,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import SummaryApi from "../../../common/index";
import "../../../Styles/SellerStylings/UploadProduct.css";

const { Option } = Select;
const { Title } = Typography;

const currencyOptions = [{ label: "₹ INR", value: "INR" }];

const AdminUploadProduct = ({ sellerId, onSuccess, editMode = false, initialValues = {} }) => {
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [existingImages, setExistingImages] = useState([]);
const [existingVideo, setExistingVideo] = useState(null);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("adminToken");

  // Fetch categories
useEffect(() => {
  const fetchCategories = async () => {
    try {
      const res = await axios.get(SummaryApi.getCategories.url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Full category response:", res.data);

      // Backend returns: { success: true, categories: [...] }
      const categoryList = res.data?.categories || [];

      console.log("Resolved categories:", categoryList);

      setCategories(categoryList);
    } catch (err) {
      console.error("Error fetching categories:", err);
      message.error("Failed to load categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  fetchCategories();
}, [token]);


  // Prefill form in edit mode
useEffect(() => {
  if (!editMode || !initialValues) return;
   // Normalize social links (important)
const safeYoutube = initialValues.youtube ?? "";
const safeFacebook = initialValues.facebook ?? "";
const safeInstagram = initialValues.instagram ?? "";

  // 1) Basic fields
 form.setFieldsValue({
  name: initialValues.name ?? "",
  category: initialValues.category_name ?? undefined,
  actual_price: initialValues.actual_price ? parseInt(initialValues.actual_price) : "",
  selling_price: initialValues.selling_price ? parseInt(initialValues.selling_price) : "",
  stock: initialValues.stock ?? "",
  deliveryOption: initialValues.deliveryOption ?? undefined,
  description: initialValues.description ?? "",
 youtube: safeYoutube,
  facebook: safeFacebook,
  instagram: safeInstagram,
  actual_currency: "INR",
  selling_currency: "INR",
});


  // 2) Normalize images (covers many shapes)
  let rawImages = initialValues.images;

  // if images was stored as a JSON string, parse it
  if (typeof rawImages === "string") {
    try {
      rawImages = JSON.parse(rawImages);
    } catch (e) {
      // fallback: try to trim and use as single path
      rawImages = rawImages ? [rawImages] : [];
    }
  }

  // if null/undefined or not an array, make it an array
  if (!Array.isArray(rawImages)) rawImages = [];

  const safeImageList = rawImages.map((img, index) => {
    // img might be a string, or object like { url: "/uploads/..." }, or something else
    let imgPath = "";
    if (typeof img === "string") {
      imgPath = img;
    } else if (img && typeof img.url === "string") {
      imgPath = img.url;
    } else if (img && typeof img.path === "string") { // possible shape
      imgPath = img.path;
    } else {
      imgPath = ""; // skip / fallback
    }

    const finalUrl = imgPath
      ? imgPath.startsWith("http")
        ? imgPath
        : `${BASE_URL}${imgPath}`
      : "";

    return {
      uid: `prefill-${index}`,
      name: imgPath ? imgPath.split("/").pop() : `image-${index}`,
      status: finalUrl ? "done" : "error",
      url: finalUrl,
      thumbUrl: finalUrl,
    };
  }).filter(item => item.url); // remove empty / invalid entries

  // set Upload's controlled state and also set form field so Form.Item shows previews
  setExistingImages(safeImageList);
  form.setFieldsValue({ images: safeImageList });

  // 3) Normalize video (similar defensive logic)
  let rawVideo = initialValues.video;
  if (typeof rawVideo === "string" && rawVideo.trim() !== "") {
    const videoPath = rawVideo.startsWith("http") ? rawVideo : `${BASE_URL}${rawVideo}`;
    const vList = [{
      uid: "prefill-video",
      name: videoPath.split("/").pop(),
      status: "done",
      url: videoPath,
    }];
    setExistingVideo(vList);
    form.setFieldsValue({ video: vList });
  } else {
    setExistingVideo([]); // ensure empty if none
    form.setFieldsValue({ video: [] });
  }
}, [editMode, initialValues, form, BASE_URL]);

  const handleFinish = async (values) => {
    if (!sellerId) return message.error("Seller ID missing!");
        values.youtube = values.youtube ?? "";
  values.facebook = values.facebook ?? "";
  values.instagram = values.instagram ?? "";
    const formData = new FormData();

    // Append non-file fields
    Object.keys(values).forEach((key) => {
      if (key !== "images" && key !== "video") {
        formData.append(key, values[key]);
      }
    });

    // Append new images only
    if (values.images && values.images.length > 0) {
      values.images.forEach((file) => {
        if (file.originFileObj) {
          formData.append("images", file.originFileObj);
        }
      });
    }

    // Append video if new
    if (values.video && values.video.length > 0) {
      const videoFile = values.video[0].originFileObj;
      if (videoFile) formData.append("video", videoFile);
    }

    try {
      setUploading(true);

      if (editMode && initialValues?.id) {
        const updateUrl = SummaryApi.updateProductByAdmin(initialValues.id).url;

        await axios.put(updateUrl, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        message.success("Product updated successfully!");
      } else {
        const uploadUrl = SummaryApi.uploadProductByAdmin(sellerId).url;

        await axios.post(uploadUrl, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        message.success("Product uploaded successfully!");
      }

      form.resetFields();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Upload/Update error:", err.response?.data || err);
      message.error(editMode ? "Failed to update product" : "Failed to upload product");
    } finally {
      setUploading(false);
    }
  };

  if (loadingCategories) {
    return <div style={{ textAlign: "center", marginTop: 50 }}>Loading categories...</div>;
  }

  return (
    <div className="upload-container">
      <Card className="upload-card" bordered={false}>
        <Title level={3}>{editMode ? "Edit Product" : "Upload Product"}</Title>
        <Divider />
        <Form layout="vertical" form={form} onFinish={handleFinish}>
          {/* Product Name */}
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: "Please enter product name" }]}
            
          >
            <Input placeholder="Enter product name" />
          </Form.Item>

          {/* Category */}
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select placeholder="Select category">
              {categories.map((cat) => (
                <Option value={cat.name}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Price */}
          <Row gutter={8}>
            <Col span={6}>
              <Form.Item
                name="actual_currency"
                label="Currency"
                initialValue="INR"
                rules={[{ required: true }]}
              >
                <Select>
                  {currencyOptions.map((c) => (
                    <Option key={c.value} value={c.value}>
                      {c.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item
                name="actual_price"
                label="Actual Price"
               
                rules={[{ required: true, message: "Enter actual price" }]}
              >
                <Input type="number" placeholder="Enter actual price"  onWheel={(e) => e.target.blur()}/>
                
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={6}>
              <Form.Item
                name="selling_currency"
                label="Currency"
                initialValue="INR"
                rules={[{ required: true }]}
              >
                <Select>
                  {currencyOptions.map((c) => (
                    <Option key={c.value} value={c.value}>
                      {c.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item
                name="selling_price"
                label="Selling Price"
                rules={[{ required: true, message: "Enter selling price" }]}
              >
                <Input type="number" placeholder="Enter selling price"  onWheel={(e) => e.target.blur()}/>
              </Form.Item>
            </Col>
          </Row>

          {/* Description */}
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Enter product description" />
          </Form.Item>

          <Form.Item name="youtube" label="YouTube Link">
            <Input placeholder="Enter YouTube URL" />
          </Form.Item>
          
          <Form.Item name="facebook" label="Facebook Link">
            <Input placeholder="Enter Facebook URL" />
          </Form.Item>
          
          <Form.Item name="instagram" label="Instagram Link">
            <Input placeholder="Enter Instagram URL" />
          </Form.Item>
          <Form.Item
            name="stock"
            label="Stock"
            rules={[{ required: true, message: "Enter stock quantity" }]}
          >
            <Input type="number" placeholder="Enter available stock"  onWheel={(e) => e.target.blur()}/>
          </Form.Item>

          {/* Delivery Option */}
          <Form.Item
            name="deliveryOption"
            label="Delivery Option"
            rules={[{ required: true, message: "Please select a delivery option" }]}
          >
            <Select placeholder="Choose delivery type">
              <Option value="store_pickup">Store Pickup</Option>
              <Option value="home_delivery">Home Delivery</Option>
              <Option value="both">Both</Option>
            </Select>
          </Form.Item>

          {/* Images */}
          <Form.Item
            name="images"
            label="Upload Images (Max 4)"
            rules={[{ required: true, message: "Please upload at least 1 image" }]}
            valuePropName="fileList"
            getValueFromEvent={(e) => e?.fileList}
          >
           <Upload
    beforeUpload={() => false}
    multiple
    maxCount={4}
    listType="picture-card"
    fileList={existingImages}
    onChange={({ fileList }) => {
      setExistingImages(fileList);
      form.setFieldsValue({ images: fileList });
    }}
  >
              <div>
                <UploadOutlined />
                <div style={{ fontSize: 12 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>

          {/* Video */}
          <Form.Item
            name="video"
            label="Upload Video (Max 5MB)"
            valuePropName="fileList"
            getValueFromEvent={(e) => e?.fileList}
          >
           <Upload
    beforeUpload={(file) => {
      if (file.size > 5 * 1024 * 1024) {
        message.error("Video size must be ≤ 5MB");
        return Upload.LIST_IGNORE;
      }
      return false;
    }}
    maxCount={1}
    accept="video/*"
    fileList={existingVideo}
    onChange={({ fileList }) => {
      setExistingVideo(fileList);
      form.setFieldsValue({ video: fileList });
    }}
  >
              <Button icon={<UploadOutlined />}>Select Video</Button>
            </Upload>
          </Form.Item>

          {/* Submit */}
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={uploading}>
              {editMode ? "Update Product" : "Upload Product"}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AdminUploadProduct;